import { cn } from '@/shadcn-ui/utils';
import { ComponentPropsWithoutRef, forwardRef } from 'react';
import BrandmarkSunset from '@/icons/brandmark-sunset';
import Spacer from '@/common/components/spacer';
import ChevronDown from '@/icons/chevron-down';

export interface SavingsV2PillProps extends ComponentPropsWithoutRef<'div'> {
  /** The text to display on the pill */
  text?: string;
  thickenBorder?: boolean;
  /** If true, the shadow is always shown */
  permanentShadow?: boolean;
  contentClassName?: string;
  textClassName?: string;
  animationDuration?: 'duration-150' | 'duration-300';
}

const SavingsV2Pill = forwardRef<HTMLDivElement, SavingsV2PillProps>(
  (
    {
      text = 'Show savings',
      thickenBorder = false,
      permanentShadow = false,
      animationDuration = 'duration-150',
      id,
      contentClassName,
      textClassName,
      className,
      ...props
    }: SavingsV2PillProps,
    ref
  ) => {
    return (
      <div
        ref={ref}
        // Note: I'm using an animation of duration of 150ms to match react-joyride's default animation duration
        className={cn(
          'group relative flex h-[39px] w-[169px] items-center justify-center rounded-[12px] bg-[linear-gradient(37.15deg,_#71D9D5_-10.58%,_#D2C6F7_80.75%,_#F9DCDB_116.15%)] p-[1.5px] transition-all',
          animationDuration,
          {
            'hover:shadow-savingsPillShadow group-hover:shadow-savingsPillShadow':
              !thickenBorder && !permanentShadow,
            'shadow-savingsPillShadow': !thickenBorder && permanentShadow,
          },
          className
        )}
        aria-label="View savings calculator"
        {...props}
      >
        {/* Additional thick border */}
        <div
          id={id}
          className={cn(
            'absolute bottom-[-2.5px] left-[-2.5px] right-[-2.5px] top-[-2.5px] z-0 h-[44px] w-[174px] rounded-[12px] bg-[linear-gradient(37.15deg,_#71D9D5_-10.58%,_#D2C6F7_80.75%,_#F9DCDB_116.15%)] opacity-0 transition-all',
            animationDuration,
            {
              'opacity-100': thickenBorder,
              'group-hover:shadow-savingsPillShadow': thickenBorder && !permanentShadow,
              'shadow-savingsPillShadow': thickenBorder && permanentShadow,
            },
            className
          )}
          style={{
            willChange: 'opacity',
          }}
        />

        {/* Content */}
        <div
          className={cn(
            'z-1 flex h-full w-full items-center justify-between rounded-[12px] bg-[linear-gradient(128deg,_#00FFCD_-171.54%,_#3A00E5_157.43%)] pl-[8px] pr-[10px]',
            contentClassName
          )}
        >
          <div className="flex w-full">
            <BrandmarkSunset className="h-[20px] w-[20px] flex-shrink-0" />
            <Spacer variant="width8" />
            <p className={cn('body2-strong text-white', textClassName)}>{text}</p>
          </div>
          <ChevronDown className="h-[16px] w-[16px] flex-shrink-0" fillPrimary="white" />
        </div>
      </div>
    );
  }
);

SavingsV2Pill.displayName = 'SavingsV2Pill';

export default SavingsV2Pill;
